import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowUpRight, FileText, Video, Download, Film, Folder, FileSpreadsheet, FormInput, BookOpen, Palette, Users, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useSiteSettings, pickText } from "@/lib/siteSettings";

const ICONS = { Article: FileText, Video, Download };

/* ------ Document Library (editable) ------
   To add a document, append to this array.
   • `platform`  one of: Canva | Google Sheets | Google Form | Heyzine | Google Doc
   • `category`  one of: Buyer | Listing & Seller | Open House | Business Ops
   ------------------------------------------------------------ */
const documents = [
  // ----- Buyer -----
  {
    title: "Buyer Presentation",
    platform: "Canva",
    category: "Buyer",
    note: "Tammy's editable buyer-consultation deck. Duplicate and brand to your own colors.",
    link: "https://canva.link/kxivxkiizkcrsnz",
  },
  {
    title: "Sample Buyer Documents",
    platform: "Canva",
    category: "Buyer",
    note: "A working set of buyer-side documents you can adapt for your own clients.",
    link: "https://canva.link/paanklfxvct4jvy",
  },
  {
    title: "Buyer Pre-Meeting Questionnaire",
    platform: "Canva",
    category: "Buyer",
    note: "Send this to buyers before your first meeting — saves time and surfaces what really matters.",
    link: "https://www.canva.com/design/DAGFbVNMcGY/KKzC1WktTGnThJmLLmErGQ/view?utm_content=DAGFbVNMcGY&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview",
  },

  // ----- Checklists -----
  {
    title: "New Buyer Checklist",
    platform: "Word Doc",
    category: "Checklists",
    note: "Step-by-step checklist for onboarding a new buyer client — download and adapt.",
    link: "https://customer-assets-wrfwihn1.emergentagent.net/job_property-mentor-4/artifacts/r221fwoy_New_Buyer_Checklist%20MREA.docx",
  },
  {
    title: "Make an Offer Checklist",
    platform: "Word Doc",
    category: "Checklists",
    note: "Everything to gather and confirm before writing an offer for your buyer.",
    link: "https://customer-assets-wrfwihn1.emergentagent.net/job_property-mentor-4/artifacts/nayw8xl4_Make_an_Offer_Checklist%20MREA.docx",
  },
  {
    title: "Under Contract Checklist",
    platform: "Word Doc",
    category: "Checklists",
    note: "The task list that carries a buyer smoothly from accepted offer to closing.",
    link: "https://customer-assets-wrfwihn1.emergentagent.net/job_property-mentor-4/artifacts/jq43s8ks_Under_Contract_Checklist%20MREA.docx",
  },
  {
    title: "Prelisting Lead Sheet",
    platform: "Word Doc",
    category: "Checklists",
    note: "Questions and details to gather before the listing appointment — sets up a smooth pitch.",
    link: "https://customer-assets-wrfwihn1.emergentagent.net/job_property-mentor-4/artifacts/0ll3jub1_Prelisting_Lead_Sheet%20MREA.docx",
  },
  {
    title: "Listing Checklist",
    platform: "Word Doc",
    category: "Checklists",
    note: "The full task list that carries a listing from signed agreement to sold sign.",
    link: "https://customer-assets-wrfwihn1.emergentagent.net/job_property-mentor-4/artifacts/uztiz9k8_Listing_Checklist%20MREA.docx",
  },

  // ----- Listing & Seller -----
  {
    title: "Listing Presentation",
    platform: "Canva",
    category: "Listing & Seller",
    note: "The complete listing presentation she uses in real appointments — fully editable.",
    link: "https://canva.link/ch7ehzitvv2mwj2",
  },
  {
    title: "Sample Seller Documents",
    platform: "Canva",
    category: "Listing & Seller",
    note: "Listing paperwork templates — the documents Tammy hands to every seller.",
    link: "https://canva.link/fsuifcjcp1kbz3h",
  },
  {
    title: "Just Listed Flyers",
    platform: "Canva",
    category: "Listing & Seller",
    note: "Branded \"Just Listed\" flyers ready to drop in your property photos and details.",
    link: "https://www.canva.com/design/DAGE92wDlIc/MmI4xE06EK_jS0QgJVky8Q/view?utm_content=DAGE92wDlIc&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview",
  },
  {
    title: "Coming Soon Flyers",
    platform: "Canva",
    category: "Listing & Seller",
    note: "Use these before a listing hits the MLS — build buzz and gauge demand.",
    link: "https://www.canva.com/design/DAGE-QLs6oA/mx7tCp3q6sXmg-Z9374-Cg/view?utm_content=DAGE-QLs6oA&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview",
  },

  // ----- Open House -----
  {
    title: "Open House Sign-In Sheet",
    platform: "Canva",
    category: "Open House",
    note: "Capture every visitor cleanly. Print and bring to every open house.",
    link: "https://canva.link/zzgbjxp4ggndvs2",
  },
  {
    title: "Open House Flyers",
    platform: "Canva",
    category: "Open House",
    note: "Print-ready flyers promoting your upcoming open house dates and details.",
    link: "https://www.canva.com/design/DAGE9iVPoBc/rOIv67LSE6DJ9y-d1SEbfQ/view?utm_content=DAGE9iVPoBc&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview",
  },
  {
    title: "Open House Pro Marketing Plan",
    platform: "Heyzine",
    category: "Open House",
    note: "Flip-book walkthrough of Tammy's full open-house promotion playbook.",
    link: "https://heyzine.com/flip-book/7e8ea8faed.html",
  },

  // ----- Business Ops -----
  {
    title: "Agent Tracker Scorecard",
    platform: "Google Sheets",
    category: "Business Ops",
    note: "Track contacts, conversion, and pipeline weekly. Make your own copy before editing.",
    link: "https://docs.google.com/spreadsheets/d/12gfbwqRTO_s89ZKwKi7IgtNsXRbxQz2i6m-9dj_gsdk/edit?usp=sharing",
  },
  {
    title: "Customize Your Presentation",
    platform: "Google Form",
    category: "Business Ops",
    note: "Fill out this short form and Tammy will personalize a presentation to your branding.",
    link: "https://docs.google.com/forms/d/e/1FAIpQLSfhk1m0s6J8EpPb76lZ4Ky44MzRCEfgCaABluncEZqGFGAbMg/viewform",
  },
];

const DOCUMENT_CATEGORY_ORDER = ["Buyer", "Listing & Seller", "Checklists", "Open House", "Business Ops"];

const PLATFORM_META = {
  "Canva":         { icon: Palette,          gradient: "from-[#7D2AE8] to-[#00C4CC]" },
  "Google Sheets": { icon: FileSpreadsheet,  gradient: "from-[#0F9D58] to-[#34A853]" },
  "Google Form":   { icon: FormInput,        gradient: "from-[#673AB7] to-[#9C27B0]" },
  "Google Doc":    { icon: FileText,         gradient: "from-[#4285F4] to-[#1A73E8]" },
  "Heyzine":       { icon: BookOpen,         gradient: "from-[#0EA5E9] to-[#6366F1]" },
  "Word Doc":      { icon: FileText,         gradient: "from-[#2B579A] to-[#41A5EE]" },
};

export default function CoachingResources() {
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const { settings } = useSiteSettings();

  // If they unlocked earlier in this tab, skip the password prompt
  useEffect(() => {
    if (sessionStorage.getItem("portalUnlocked") === "true") {
      const cached = sessionStorage.getItem("portalResources");
      if (cached) {
        try { setItems(JSON.parse(cached)); } catch { /* ignore */ }
      }
      setUnlocked(true);
    }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/resources/gate", { password });
      const list = res.data.resources || [];
      setItems(list);
      sessionStorage.setItem("portalUnlocked", "true");
      sessionStorage.setItem("portalResources", JSON.stringify(list));
      setUnlocked(true);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error("Incorrect password. Reach out if you need a reset.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!unlocked) {
    return (
      <div className="max-w-md mx-auto px-6 py-32" data-testid="coaching-locked">
        <div className="bg-card border border-border rounded-3xl p-10 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-primary/15 flex items-center justify-center">
            <Lock className="text-primary" size={26} strokeWidth={1.6} />
          </div>
          <h1 className="font-serif text-4xl mt-6">Agent Portal</h1>
          <p className="text-foreground/70 mt-4 leading-relaxed">
            This area is for active members of Tammy's coaching program. Enter
            your client password to continue.
          </p>
          <form onSubmit={onSubmit} className="mt-8 space-y-4 text-left">
            <Label className="text-xs uppercase tracking-[0.18em] text-foreground/70">
              Client password
            </Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your access code"
              className="rounded-xl h-12"
              data-testid="coaching-password-input"
              required
            />
            <Button
              type="submit"
              disabled={submitting}
              data-testid="coaching-unlock-btn"
              className="w-full rounded-full bg-primary hover:bg-primary/90 btn-lift h-12"
            >
              {submitting ? "Checking..." : "Unlock my resources"}
            </Button>
          </form>
          <p className="text-xs text-foreground/55 mt-6">
            Not a member yet?{" "}
            <a href="/book" className="text-primary hover:underline">
              Book a discovery call
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="coaching-unlocked">
      <div className="grid md:grid-cols-12 gap-12 mb-12">
        <div className="md:col-span-7">
          <span className="text-[11px] uppercase tracking-[0.3em] text-primary">
            Members Only
          </span>
          <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-[1.02]">
            Your agent
            <br />
            <span className="italic text-primary">portal.</span>
          </h1>
        </div>
        <p className="md:col-span-4 md:col-start-9 text-lg text-foreground/75 leading-relaxed self-end">
          Replays, templates, and tools — kept up to date and curated for
          active clients.
        </p>
      </div>

      {/* Feature: Video Library */}
      <Link
        to="/agent-portal/video-library"
        data-testid="video-library-link"
        className="group block mb-12 bg-[#0F1E33] text-[#F5F8FB] rounded-2xl overflow-hidden card-lift"
      >
        <div className="grid md:grid-cols-12 gap-8 items-center p-8 md:p-10">
          <div className="md:col-span-2 flex md:justify-start">
            <div className="w-16 h-16 rounded-2xl bg-[#93C5FD]/20 flex items-center justify-center">
              <Film className="text-[#93C5FD]" size={28} strokeWidth={1.5} />
            </div>
          </div>
          <div className="md:col-span-7">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#93C5FD]">
              Featured
            </span>
            <h2 className="font-serif text-3xl md:text-4xl mt-2 leading-tight">
              Video Library
            </h2>
            <p className="text-sm md:text-base opacity-85 mt-3 leading-relaxed max-w-xl">
              Folders of training videos hosted on Google Drive — including
              the full Certification Series. Click in and watch any time.
            </p>
          </div>
          <div className="md:col-span-3 md:text-right">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-[#93C5FD] group-hover:gap-3 transition-all">
              Open library <ArrowUpRight size={16} />
            </span>
          </div>
        </div>
      </Link>

      {/* Document Library */}
      <section className="mb-16" data-testid="document-library">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-8">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary">
              <FileText size={12} /> Document Library
            </div>
            <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">
              Presentations, templates & trackers.
            </h2>
          </div>
          <p className="md:col-span-4 md:col-start-9 text-foreground/70 leading-relaxed text-sm">
            Click any card to open the source document. Make a copy before
            editing so the master stays clean.
          </p>
        </div>

        <div className="space-y-12">
          {DOCUMENT_CATEGORY_ORDER.map((cat) => {
            const inCat = documents.filter((d) => d.category === cat);
            if (inCat.length === 0) return null;
            return (
              <div key={cat} data-testid={`doc-category-${cat.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-serif text-2xl">{cat}</span>
                  <span className="flex-1 h-px bg-border" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-foreground/55">
                    {inCat.length} {inCat.length === 1 ? "item" : "items"}
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {inCat.map((d, i) => {
                    const meta = PLATFORM_META[d.platform] || { icon: FileText, gradient: "from-foreground/30 to-foreground/10" };
                    const Icon = meta.icon;
                    return (
                      <a
                        key={d.title}
                        href={d.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`document-card-${cat.toLowerCase().replace(/[^a-z]/g, '-')}-${i}`}
                        className="group bg-card border border-border rounded-2xl overflow-hidden card-lift flex flex-col"
                      >
                        <div className={`relative h-28 bg-gradient-to-br ${meta.gradient} flex items-center justify-center`}>
                          <Icon className="text-white/95" size={36} strokeWidth={1.5} />
                          <span className="absolute top-3 right-3 bg-white/90 text-foreground text-[9px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full">
                            {d.platform}
                          </span>
                        </div>
                        <div className="p-6 flex flex-col flex-1">
                          <h3 className="font-serif text-xl leading-tight">{d.title}</h3>
                          <p className="text-sm text-foreground/70 mt-2 leading-relaxed flex-1">
                            {d.note}
                          </p>
                          <span className="inline-flex items-center gap-1.5 mt-5 text-sm text-primary font-medium">
                            Open <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </span>
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Community & Links */}
      <section className="mb-16" data-testid="community-links">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-8">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary">
              <Users size={12} /> Community & Links
            </div>
            <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">
              Keep the conversation going.
            </h2>
          </div>
          <p className="md:col-span-4 md:col-start-9 text-foreground/70 leading-relaxed text-sm">
            Places outside this portal where you can connect, learn, and stay
            plugged in.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <a
            href={pickText(settings, "links.facebookGroup", "https://www.facebook.com/groups/1492779424358054")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-facebook-coaching-group"
            className="group bg-card border border-border rounded-2xl p-8 card-lift block"
          >
            <div className="flex items-start justify-between mb-5">
              <div className="w-12 h-12 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center">
                <Users className="text-primary" size={22} strokeWidth={1.6} />
              </div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-foreground/55 bg-muted px-2.5 py-1 rounded-full">
                Facebook Group
              </span>
            </div>
            <h3 className="font-serif text-2xl leading-tight">
              Join our private business coaching Facebook group.
            </h3>
            <p className="text-sm text-foreground/70 mt-3 leading-relaxed">
              Members-only community for questions, wins, resources, and
              live conversation between sessions.
            </p>
            <span className="inline-flex items-center gap-1.5 mt-6 text-sm text-primary font-medium">
              Open group <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </a>

          <a
            href={pickText(settings, "links.kwMaineResources", "https://www.kwmaineresources.com/")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-kw-maine-resources"
            className="group bg-card border border-border rounded-2xl p-8 card-lift block"
          >
            <div className="flex items-start justify-between mb-5">
              <div className="w-12 h-12 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center">
                <BookOpen className="text-primary" size={22} strokeWidth={1.6} />
              </div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-foreground/55 bg-muted px-2.5 py-1 rounded-full">
                KW Maine
              </span>
            </div>
            <h3 className="font-serif text-2xl leading-tight">
              More resources from our office.
            </h3>
            <p className="text-sm text-foreground/70 mt-3 leading-relaxed">
              Trainings, playbooks, and KW Maine tools — updated by the
              office team beyond what lives in this portal.
            </p>
            <span className="inline-flex items-center gap-1.5 mt-6 text-sm text-primary font-medium">
              Visit kwmaineresources.com <ExternalLink size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </a>
        </div>
      </section>

      {items.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((r) => {
            const Icon = ICONS[r.type] || FileText;
            return (
              <a
                key={r.id}
                href={r.link}
                data-testid={`gated-card-${r.id}`}
                className="group bg-card border border-border rounded-2xl p-8 card-lift block"
              >
                <div className="flex items-start justify-between mb-6">
                  <Icon className="text-primary" size={28} strokeWidth={1.5} />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-foreground/60">
                    {r.category}
                  </span>
                </div>
                <h3 className="font-serif text-2xl leading-snug">{r.title}</h3>
                <p className="text-sm text-foreground/70 mt-3 leading-relaxed">
                  {r.summary}
                </p>
                <div className="flex items-center gap-2 mt-6 text-sm text-primary font-medium">
                  Open
                  <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
